// @ts-strict-ignore
import {
  AfterViewChecked,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { LMPrintDialogService, LMPrintRequestService } from '@app/components';
import { Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { AppConstants, LMGlobalUtilService } from '@app/utils';
import { LMContextService, LMLoggerService, LMUrlUtilService, URLOptions } from '@app/services';
import { SohoBusyIndicatorDirective } from 'ids-enterprise-ng';
import { ListResource, Resource } from '@app/resources';
import { LMGlobalPersonalizationService } from '@app/states';
import { LMReportsCatalogService } from '@report/index';

@Component({
  selector: 'lm-poll-and-print-dialog',
  standalone: false,
  templateUrl: './lm-poll-and-print-dialog.component.html',
  styleUrls: [ './lm-poll-and-print-dialog.component.scss' ]
  })

export class LMPollAndPrintDialogComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild(SohoBusyIndicatorDirective, { static: true }) busyIndicator: SohoBusyIndicatorDirective;

  public isExport: boolean;
  private reportCatalogEnabled: boolean;

  public status: string;
  public statusMessage: string;
  public displayIcon: boolean;
  public iconName: string;
  private iconClasses: string;
  private errorMsg: string;

  private myPrintFiles: string;
  private myPrintFilesUrl: string;

  private cancelPolling$: Subject<boolean>;
  private timerSubscription: ISubscription;
  private inProgress: number;
  private maxPollIterations: number;

  constructor(private lmPrintRequestService: LMPrintRequestService,
    private lmPrintDialogService: LMPrintDialogService,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmReportCatalogService: LMReportsCatalogService,
    private elementRef: ElementRef,
    private zone: NgZone,) {}

  ngOnInit() {
    this.cancelPolling$ = new Subject<boolean>();
    this.inProgress = 1;

    this.initialize();

    // Default 3 iterations, Cap it at 15 seconds or 4 iterations. Can be set in user settings dialog
    let maxWaitInSecs = this.lmGlobalPersonalizationService.getGlobalPersonalization().maxWaitPDFCSV;
    if (!maxWaitInSecs) {
      maxWaitInSecs = 10;
    }
    this.maxPollIterations = (maxWaitInSecs <= 5 ) ? 2
      : (maxWaitInSecs <= 10 ) ? 3 : ( maxWaitInSecs <= 15 ) ? 4 : 3;
  }

  ngOnDestroy() {
    this.timerSubscription.unsubscribe();
    this.cancelPolling$.complete();
  }

  ngAfterViewChecked() {
    const element = this.elementRef.nativeElement.querySelector('#gotoPrintFiles');
    if (element && !element.dataset.listenerAdded) {
      element.addEventListener('click', this.gotoPrintFiles.bind(this));
      element.dataset.listenerAdded = 'true';
    }
  }

  get svgIconClasses(): string {
    return this.iconClasses;
  }

  private initialize() {
    this.myPrintFiles = Soho.Locale.translate('MyPrintFiles');
    const printFilesResource = new ListResource({
      resourceId: 'UserReport',
      listName: 'MyUserReportsList'
    });

    this.myPrintFilesUrl = this.lmUrlUtilService.buildUrl({ resource: printFilesResource });
    this.reportCatalogEnabled = this.lmContextService.getContext().enableReportCatalog;
    if (this.reportCatalogEnabled) {
      this.myPrintFilesUrl = this.lmReportCatalogService.buildCatalogNavigationUrl({selectedPanel: 'printfiles'});
    }

    this.setStatusStrings('SUBMITTED');

    this.displayIcon = false;
    // this.iconName = 'info';
    // this.iconClasses = 'icon icon-info';
  }

  private setStatusStrings(type: LMPollStatus) {
    const anchorText = `<a class="hyperlink" id="gotoPrintFiles" href="#">${this.myPrintFiles}</a>`;
    switch(type) {
      case 'SUBMITTED':
        this.status = Soho.Locale.translate('DocumentGenerationStatus1');
        this.statusMessage = Soho.Locale.translate('DocumentGenerationStatusMessage1');
        // this.status = 'We will wait for a few seconds';
        // this.statusMessage = 'If you want to continue working click Dismiss and find it later at {0}';

        this.statusMessage = this.statusMessage.replace('{0}', anchorText);
        break;
      case 'INPROGRESS':
        this.status = Soho.Locale.translate('DocumentGenerationStatus2');
        this.statusMessage = Soho.Locale.translate('DocumentGenerationStatusMessage2');
        // this.status = 'This is taking a while';
        // this.statusMessage = 'We will continue to work on it. Find it later at {0}.';

        this.statusMessage = this.statusMessage.replace('{0}', anchorText);
        break;
      case 'ERROR':
        this.status = Soho.Locale.translate('DocumentGenerationStatus3');
        this.statusMessage = Soho.Locale.translate('DocumentGenerationStatusMessage3');
        // this.statusMessage += ' ' + this.errorMsg;
        break;
      default:
        this.status = Soho.Locale.translate('DocumentGenerationStatus1');
        this.statusMessage = Soho.Locale.translate('DocumentGenerationStatusMessage1');
    }
  }

  public startPoll(asyncId: string) {
    this.lmLoggerService.log([ 'Start polling for PDF/CSV generation.' ]);
    this.timerSubscription = this.lmPrintRequestService.startPoll(asyncId, this.cancelPolling$)
      .subscribe((response: LMPrintPollResponse) => {
        if (response.status === AppConstants.STATUS_INPROGRESS) {
          ++this.inProgress;
          if (this.inProgress > this.maxPollIterations) {
            this.zone.run(() => {
              this.setStatusStrings('INPROGRESS');

              this.displayIcon = true;
              this.iconName = 'info';
              this.iconClasses = 'icon icon-info';
            });
            this.cancelPoll();
            // this.lmPrintDialogService.showPollDialogStatus(response.status);
          }
        } else if (response.status === AppConstants.STATUS_COMPLETED) {
          this.cancelPoll();
          this.lmPrintDialogService.openPrintDocument(response.actionRedirect);
        } else {
          // Handle Async failure and exception
          this.errorMsg = (response.errorMsg) ? response.errorMsg : '';
          this.zone.run(() => {
            this.setStatusStrings('ERROR');

            this.displayIcon = true;
            this.iconName = 'error';
            this.iconClasses = 'icon icon-error';
          });
          this.cancelPoll();
          // this.lmPrintDialogService.showPollDialogStatus(response.status, response as LMError);
        }
      });
  }

  public cancelPoll() {
    // Either the user clicked on the cancel button in dialog
    // Or the poll returns 'in progress' for maxPollIterations times in a row
    // Or got a completed response
    // Or got an error response
    this.lmLoggerService.log([ 'End polling for PDF/CSV generation.' ]);
    this.cancelPolling$.next(true);
  }

  public gotoPrintFiles(event: Event) {
    event?.preventDefault();
    this.cancelPoll();
    this.lmGlobalUtilService.navigateByUrl(this.myPrintFilesUrl);
  }
}
